import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Stack,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  PublicOff as PublicOffIcon,
} from "@mui/icons-material";
import {
  getGalleryStatusDisplay,
  formatDate,
} from "../utils/publicGalleryHelpers";
import {
  STATUS_OPTIONS,
  GALLERY_CATEGORIES,
} from "../types/publicGalleryTypes";

const GalleryList = ({
  galleries = [],
  onSelect,
  onDelete,
  onPublish,
  onUnpublish,
  filters = {},
  onFilterChange,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters?.search || "");

  const handleSearch = (e) => {
    e.preventDefault();
    onFilterChange && onFilterChange({ search: searchTerm });
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    onFilterChange &&
      onFilterChange({ search: "", status: "all", category: "all" });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!galleries || galleries.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No galleries found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Create your first public gallery to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <form onSubmit={handleSearch} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search galleries..."
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" size="small">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </form>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters?.status || "all"}
              onChange={(e) => onFilterChange({ status: e.target.value })}
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters?.category || "all"}
              onChange={(e) => onFilterChange({ category: e.target.value })}
              label="Category"
            >
              <MenuItem value="all">All Categories</MenuItem>
              {GALLERY_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClearFilters}
            startIcon={<ClearIcon />}
            size="small"
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {galleries.map((gallery) => {
          const statusDisplay = getGalleryStatusDisplay(gallery.status);
          return (
            <Grid item xs={12} sm={6} md={4} key={gallery.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                    cursor: "pointer",
                  },
                }}
                onClick={() => onSelect && onSelect(gallery.id)}
              >
                <Box
                  sx={{
                    position: "relative",
                    pt: "56.25%",
                    bgcolor: "grey.200",
                  }}
                >
                  {gallery.coverImageUrl ? (
                    <CardMedia
                      component="img"
                      image={gallery.coverImageUrl}
                      alt={gallery.name}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.300",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No Cover
                      </Typography>
                    </Box>
                  )}
                  <Chip
                    label={statusDisplay.label}
                    color={statusDisplay.color}
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 8 }}
                  />
                  <Chip
                    icon={gallery.isPublic ? <PublicIcon /> : <PublicOffIcon />}
                    label={gallery.isPublic ? "Public" : "Private"}
                    size="small"
                    color={gallery.isPublic ? "info" : "default"}
                    sx={{ position: "absolute", top: 8, left: 8 }}
                  />
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" noWrap>
                    {gallery.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mb: 2,
                    }}
                  >
                    {gallery.description || "No description"}
                  </Typography>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {gallery.category}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {gallery.statistics?.totalImages || 0} images
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      👁 {gallery.statistics?.totalViews || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(gallery.createdAt)}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={() => onSelect && onSelect(gallery.id)}
                        startIcon={<ViewIcon />}
                      >
                        View
                      </Button>
                      {gallery.isPublic ? (
                        <Tooltip title="Unpublish">
                          <IconButton
                            color="warning"
                            size="small"
                            onClick={() =>
                              onUnpublish && onUnpublish(gallery.id)
                            }
                          >
                            <PublicOffIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Publish">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => onPublish && onPublish(gallery.id)}
                          >
                            <PublicIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => onDelete && onDelete(gallery.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default GalleryList;
